/**
 * 翻译存储提供者
 * 提供翻译记录的基础数据库操作
 *
 * 该类仅处理数据库操作，不包含业务逻辑
 */
import { dbManager } from '../database/index.js';
/**
 * 翻译存储提供者
 * 实现基础的数据库操作
 */
export class TranslationStorageProvider {
    get translations() {
        return dbManager.textSelectionTranslations;
    }
    /**
     * 私有构造函数
     */
    constructor() {
        this.initialized = false;
        this.initPromise = null;
    }
    /**
     * 获取单例实例
     */
    static getInstance() {
        if (!TranslationStorageProvider.instance) {
            TranslationStorageProvider.instance = new TranslationStorageProvider();
        }
        return TranslationStorageProvider.instance;
    }
    async initialize() {
        if (this.initialized)
            return;
        if (this.initPromise)
            return this.initPromise;
        this.initPromise = (async () => {
            this.initialized = true;
        })();
        return this.initPromise;
    }
    async ensureInitialized() {
        if (!this.initialized) {
            await this.initialize();
        }
    }
    /**
     * 保存或更新翻译记录
     * 先查询现有记录，如果存在则更新，否则创建新记录
     * @param record 包含业务ID `id` 的翻译记录
     * @returns 记录的数字主键 `orderId`
     */
    async saveTranslation(record) {
        await this.ensureInitialized();
        try {
            // 先查询是否存在具有相同 id 的记录
            const existingRecord = await this.translations.where('id').equals(record.id).first();
            let orderId;
            if (existingRecord) {
                // 更新现有记录，保留原有的 orderId
                const recordWithOrderId = {
                    ...record,
                    orderId: existingRecord.orderId,
                };
                orderId = await this.translations.put(recordWithOrderId);
            }
            else {
                // 创建新记录 - Dexie add() 接受没有主键的对象
                orderId = await this.translations.add(record);
            }
            return orderId;
        }
        catch (error) {
            console.error('TranslationStorageProvider: Failed to save record', {
                recordId: record.id,
                recordText: record.originalText,
                error: error,
                errorMessage: error instanceof Error ? error.message : 'Unknown error',
            });
            throw error;
        }
    }
    /**
     * 根据唯一的业务ID获取翻译记录
     * @param id 字符串业务ID
     * @returns 翻译记录或null
     */
    async getTranslationById(id) {
        await this.ensureInitialized();
        const record = await this.translations.where('id').equals(id).first();
        return record || null;
    }
    /**
     * 获取段落的所有翻译记录
     * @param docIdentity 文档标识
     * @param paragraphId 段落ID
     * @returns 翻译记录数组
     */
    async getTranslationsForParagraph(docIdentity, paragraphId) {
        await this.ensureInitialized();
        return await this.translations
            .where({
            'documentIdentity.id': docIdentity.id,
            'documentIdentity.type': docIdentity.type,
            paragraphId: paragraphId,
        })
            .toArray();
    }
    /**
     * 获取文档的所有翻译记录
     * @param docIdentity 文档标识
     * @returns 翻译记录数组
     */
    async getTranslationsForTextDocument(docIdentity) {
        await this.ensureInitialized();
        return await this.translations
            .where('[documentIdentity.id+documentIdentity.type]')
            .equals([docIdentity.id, docIdentity.type])
            .toArray();
    }
    /**
     * 删除翻译记录
     * @param id 翻译记录ID
     */
    async deleteTranslation(id) {
        await this.ensureInitialized();
        await this.translations.delete(id);
    }
    /**
     * 删除文档的所有翻译记录
     * @param docIdentity 文档ID
     * @returns 删除的记录数量
     */
    async deleteTranslationsForTextDocument(docIdentity) {
        await this.ensureInitialized();
        const itemsToDelete = await this.translations
            .where('[documentIdentity.id+documentIdentity.type]')
            .equals([docIdentity.id, docIdentity.type])
            .primaryKeys();
        if (itemsToDelete.length === 0) {
            return 0;
        }
        await this.translations.bulkDelete(itemsToDelete);
        return itemsToDelete.length;
    }
    /**
     * 获取所有翻译记录，支持排序、分页和搜索
     * @param options 查询选项
     * @returns 翻译记录数组
     */
    async getAllTranslations(options) {
        await this.ensureInitialized();
        let query = this.translations.toCollection();
        // 如果有搜索文本，应用搜索过滤
        if (options?.searchText) {
            const searchLower = options.searchText.toLowerCase();
            query = query.filter(item => item.originalText.toLowerCase().includes(searchLower) ||
                (typeof item.translatedText === 'string'
                    ? item.translatedText.toLowerCase().includes(searchLower)
                    : item.translatedText.translatedText
                        .toLowerCase()
                        .includes(searchLower)));
        }
        // 获取结果并手动排序
        const result = await query.toArray();
        // 应用排序
        if (options?.sortBy === 'time-asc') {
            result.sort((a, b) => a.timestamp - b.timestamp);
        }
        else {
            // 默认按时间倒序
            result.sort((a, b) => b.timestamp - a.timestamp);
        }
        // 应用分页
        if (options?.offset !== undefined && options?.limit !== undefined) {
            const paginatedResult = result.slice(options.offset, options.offset + options.limit);
            return paginatedResult;
        }
        return result;
    }
    /**
     * 搜索翻译记录
     * @param searchText 搜索文本
     * @param options 查询选项
     * @returns 匹配的翻译记录数组
     */
    async searchTranslations(searchText, options) {
        const searchOptions = {
            ...options,
            searchText,
        };
        return this.getAllTranslations(searchOptions);
    }
    /**
     * 获取翻译记录数量
     * @returns 翻译记录数量
     */
    async getTranslationCount() {
        await this.ensureInitialized();
        return await this.translations.count();
    }
    /**
     * 在事务中执行操作
     * @param callback 事务回调函数
     * @returns 回调函数的结果
     */
    async transaction(callback) {
        await this.ensureInitialized();
        return dbManager.transaction('rw', ['textSelectionTranslations'], async () => {
            return await callback();
        });
    }
}
// 数据库表
// 单例模式
TranslationStorageProvider.instance = null;
//# sourceMappingURL=TranslationStorageProvider.js.map